export const MOODLE_TRAINING_PART_ERROR_MESSAGES = {
  'trainingPart.moodleCourseCreationFailed': 'An error occurred while creating the course in Moodle',
  'trainingPart.moodleCertificateTemplateNotFound':
    'Certificate template is missing in Moodle for this company category. Please create the required *_CERT template and try again.',
  'trainingPart.moodleCertificateTemplateEmpty':
    'Certificate template exists in Moodle but has no pages. Please configure the template and try again.',
  'trainingPart.moodleCertificateTemplateInvalid':
    'Certificate template could not be applied in Moodle. Please verify template configuration and try again.',
  'trainingPart.moodleCertificateModuleMissing': 'Moodle Custom Certificate plugin is missing or disabled.',
  'trainingPart.moodleCertificateQuizMissing':
    'The duplicated Moodle course has no quiz module required for certificate setup.',
  'trainingPart.moodleCourseCreationTimeout':
    'Moodle did not respond in time while creating the course. Please try again.',
  'trainingPart.languageVariantMissing':
    'One or more required LMS language variants are missing from the system template.',
  'trainingPart.languageVariantDuplicate': 'Only one LMS course may be selected for each language.',
  'trainingPart.languageVariantMetadataMismatch':
    'All selected language variants must use the same module, version, and revision.',
  'trainingPart.languageVariantForcedLanguageMismatch':
    'A selected Moodle course has a forced language that does not match its metadata.',
  'trainingPart.languageVariantLtiMissing': 'A selected language variant is not ready for LTI launch.',
  'trainingPart.languageSyncFailed':
    'The LMS language synchronization failed. Existing valid Moodle copies were retained for retry.',
  'trainingPart.languageSyncPartialMoodleState':
    'Moodle created part of the language set but validation did not complete. Retry after checking Moodle.',
};

const buildCertificateTemplateNotFoundMessage = certificateTemplateName =>
  certificateTemplateName
    ? `Certificate template is missing in Moodle for this company category. Please create the required ${certificateTemplateName} template and try again.`
    : MOODLE_TRAINING_PART_ERROR_MESSAGES['trainingPart.moodleCertificateTemplateNotFound'];

const extractCertificateTemplateName = message => {
  if (!message) {
    return null;
  }

  const match = String(message).match(
    /(?:trainingPart\.moodleCertificateTemplateNotFound:|certificate template not found:\s*)([A-Za-z0-9_-]+_CERT)\b/i,
  );

  return match ? match[1] : null;
};

const extractMissingLanguages = errorMessage => {
  const messages = (Array.isArray(errorMessage) ? errorMessage : [errorMessage])
    .filter(Boolean)
    .map(item => String(item));
  const match = messages
    .map(message => message.match(/trainingPart\.languageVariantMissing:([a-z0-9-]+(?:,[a-z0-9-]+)*)/i))
    .find(Boolean);

  return match ? match[1].split(',').filter(Boolean) : [];
};

const buildLanguageVariantMissingMessage = errorMessage => {
  const baseMessage = MOODLE_TRAINING_PART_ERROR_MESSAGES['trainingPart.languageVariantMissing'];
  const missingLanguages = extractMissingLanguages(errorMessage);

  return missingLanguages.length ? `${baseMessage} Missing languages: ${missingLanguages.join(', ')}.` : baseMessage;
};

const resolveRawMoodleCourseCreationErrorMessage = errorMessage => {
  const messages = (Array.isArray(errorMessage) ? errorMessage : [errorMessage])
    .filter(Boolean)
    .map(item => String(item).trim());

  const shortNameConflictMessage = messages.find(message =>
    /short name is already used for another course/i.test(message),
  );
  if (shortNameConflictMessage) {
    const conflictingCourseMatch = shortNameConflictMessage.match(
      /short name is already used for another course\s*\(([^)]+)\)/i,
    );

    if (conflictingCourseMatch) {
      return `A Moodle course with the generated short name already exists: ${conflictingCourseMatch[1]}. Rename or remove the conflicting Moodle course and try again.`;
    }

    return 'A Moodle course with the generated short name already exists. Rename or remove the conflicting Moodle course and try again.';
  }

  const certificateTemplateNotFound = messages.find(message =>
    /local_coursebuilder\/certtemplatenotfound|certificate template not found/i.test(message),
  );
  if (certificateTemplateNotFound) {
    return buildCertificateTemplateNotFoundMessage(extractCertificateTemplateName(certificateTemplateNotFound));
  }

  return null;
};

const LANGUAGE_SYNC_REASON_MESSAGES = {
  target_not_found: 'The copied course was not returned by the Moodle catalog.',
  identifier_metadata_mismatch:
    'The copied course short name or ID does not match its configured module, version, revision, or language metadata.',
  duplicate_identity: 'Another Moodle course has the same module, version, revision, and language identity.',
  missing_module_code: 'The copied course has no module code.',
  invalid_language: 'The copied course has an invalid language code.',
  forced_language_mismatch: 'The copied course forced language does not match its language metadata.',
  missing_lti: 'The copied course is not ready for LTI launch.',
  lti_missing: 'The copied course is not ready for LTI launch.',
  language_mismatch: 'The copied course language does not match the selected system variant.',
  module_code_mismatch: 'The copied course module code does not match the selected system variant.',
  version_mismatch: 'The copied course version does not match the selected system variant.',
  revision_mismatch: 'The copied course revision does not match the selected system variant.',
  missing_course_id: 'Moodle did not return the copied course ID.',
};

const resolveLanguageSyncErrorMessage = errorMessage => {
  const message = (Array.isArray(errorMessage) ? errorMessage : [errorMessage])
    .filter(Boolean)
    .map(item => String(item))
    .find(item => /trainingPart\.(?:languageSyncFailed|languageSyncPartialMoodleState):/i.test(item));
  if (!message) {
    return null;
  }

  const match = message.match(
    /trainingPart\.(languageSyncFailed|languageSyncPartialMoodleState):([a-z0-9-]+):(\d+):([a-z0-9_-]+)/i,
  );
  if (!match) {
    return null;
  }

  const [, errorType, language, courseId, reason] = match;
  const baseMessage = MOODLE_TRAINING_PART_ERROR_MESSAGES[`trainingPart.${errorType}`];
  const reasonMessage = LANGUAGE_SYNC_REASON_MESSAGES[reason];
  const courseContext = Number(courseId) > 0 ? ` Moodle course: ${courseId}.` : '';
  const languageContext = ` Language: ${language}.`;

  return `${baseMessage}${languageContext}${courseContext}${reasonMessage ? ` ${reasonMessage}` : ''}`;
};

export const resolveMoodleTrainingPartErrorMessage = errorMessage => {
  if (!errorMessage) {
    return null;
  }

  const languageSyncMessage = resolveLanguageSyncErrorMessage(errorMessage);
  if (languageSyncMessage) {
    return languageSyncMessage;
  }

  if (extractMissingLanguages(errorMessage).length) {
    return buildLanguageVariantMissingMessage(errorMessage);
  }

  const messageKeys = (Array.isArray(errorMessage) ? errorMessage : [errorMessage])
    .filter(Boolean)
    .map(item => String(item).trim());

  const knownKeys = Object.keys(MOODLE_TRAINING_PART_ERROR_MESSAGES);
  const matchedKey = messageKeys
    .map(rawMessage => rawMessage.replace(/[)\],;:!?]+$/g, ''))
    .find(rawMessage => {
      if (MOODLE_TRAINING_PART_ERROR_MESSAGES[rawMessage]) {
        return true;
      }

      return knownKeys.some(key => rawMessage.includes(key));
    });

  if (!matchedKey) {
    return resolveRawMoodleCourseCreationErrorMessage(errorMessage);
  }

  const exact = MOODLE_TRAINING_PART_ERROR_MESSAGES[matchedKey];
  if (exact) {
    if (matchedKey === 'trainingPart.moodleCertificateTemplateNotFound') {
      return buildCertificateTemplateNotFoundMessage(extractCertificateTemplateName(errorMessage));
    }
    if (matchedKey === 'trainingPart.languageVariantMissing') {
      return buildLanguageVariantMissingMessage(errorMessage);
    }
    return exact;
  }

  const partialKey = knownKeys.find(key => matchedKey.includes(key));
  if (!partialKey) {
    return resolveRawMoodleCourseCreationErrorMessage(errorMessage);
  }

  if (partialKey === 'trainingPart.moodleCertificateTemplateNotFound') {
    return buildCertificateTemplateNotFoundMessage(extractCertificateTemplateName(errorMessage));
  }

  if (partialKey === 'trainingPart.languageVariantMissing') {
    return buildLanguageVariantMissingMessage(errorMessage);
  }

  return MOODLE_TRAINING_PART_ERROR_MESSAGES[partialKey];
};
